import React, { Component } from "react";

import { LabeledTextInput } from "./TextInput";
import { LabeledTextArea } from "./TextArea";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { LexWorkplaceCheckIcon } from "./LexWorkplaceIcons";
import SectionHeading from "./SectionHeading";
import lxwConstants from "../config/lexworkplace-constants";

// A free-text field that loads its value asynchronously and is saved with its own
// submit button (Practice Areas, About the Firm, Custom AI Instructions, etc).
export class EditableInfoField extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			value: "",
			baseValue: "",
			seededFrom: null,
			saving: false,
		};
		this.handleChange = this.handleChange.bind(this);
		this.submit = this.submit.bind(this);
	}
	/*
		Re-seeds whenever the loaded value changes, not just on the first
		load→loaded transition. The Redux loading flags are only ever set false
		(nothing flips them back to true for a refetch), so on a revisit this
		component mounts against cached data and would otherwise keep showing
		it — and write it back over whatever the refetch returned. Unsaved
		edits always win over an incoming value.
	*/
	static getDerivedStateFromProps(props, state) {
		if (props.loading) return null;
		const initialValue = props.initialValue || "";
		const isDirty = state.value !== state.baseValue;
		if (initialValue !== state.seededFrom && !isDirty) {
			return {
				loading: false,
				value: initialValue,
				baseValue: initialValue,
				seededFrom: initialValue,
			};
		}
		return state.loading ? { loading: false } : null;
	}
	handleChange(name, value) {
		this.setState({ value });
	}
	submit() {
		// pin the value being saved: anything typed while the request is in
		// flight must stay dirty, and seededFrom must match what the store is
		// about to hand back so the save doesn't read as an incoming change
		const savedValue = this.state.value;
		this.setState({ saving: true });
		this.props
			.onSave(savedValue)
			.then(() => {
				this.setState({
					baseValue: savedValue,
					seededFrom: savedValue,
				});
			})
			.catch(() => {})
			.finally(() => {
				this.setState({ saving: false });
			});
	}
	render() {
		const {
			as,
			name,
			placeholder,
			disabled,
			disabledMessage,
			maxLength = lxwConstants.fieldLimits.infoField,
			height,
		} = this.props;
		const isTextarea = as === "textarea";
		// only LabeledTextArea forwards style through to the field itself
		const heightStyle = height ? { style: { height } } : {};
		const Field = isTextarea ? LabeledTextArea : LabeledTextInput;
		// LabeledTextArea forwards props straight to a native <textarea> (autoComplete),
		// while LabeledTextInput has its own custom-cased prop (autocomplete).
		// No maxLength attribute here on purpose - let the user type past the
		// limit and see the real count rather than silently truncating.
		const autocompleteProps = isTextarea
			? { autoComplete: "off" }
			: { autocomplete: "off" };

		if (this.state.loading) {
			// Rendered as a totally separate element from the loaded field below so that,
			// once real data arrives, the field remounts fresh (defaultValue only applies
			// on mount for these uncontrolled inputs).
			return (
				<Field
					name={name}
					placeholder="Loading..."
					defaultValue=""
					disabled
					{...autocompleteProps}
					{...heightStyle}
				/>
			);
		}

		const charCount = this.state.value.length;
		const overLimit = charCount > maxLength;
		const changed = this.state.value !== this.state.baseValue;
		const canSubmit = changed && !overLimit && !this.state.saving;

		return (
			<div title={disabled ? disabledMessage : undefined}>
				<Field
					name={name}
					placeholder={placeholder}
					defaultValue={this.state.baseValue}
					onChange={this.handleChange}
					disabled={disabled || this.state.saving}
					{...autocompleteProps}
					{...heightStyle}
				/>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
						gap: 12,
						marginTop: 4,
					}}
				>
					<div
						style={{
							fontSize: 12,
							color: overLimit ? "red" : "#666",
						}}
					>
						{charCount}/{maxLength}
					</div>
					{disabled ? null : (
						<ButtonWithIcon
							handler={this.submit}
							disabled={!canSubmit}
							buttonText={
								this.state.saving ? "Saving..." : "Update"
							}
							iconComponent={<LexWorkplaceCheckIcon />}
						/>
					)}
				</div>
			</div>
		);
	}
}

// An EditableInfoField under a heading and a line of gray helper text
export const InfoFieldSection = ({
	heading,
	description,
	style,
	...fieldProps
}) => (
	<div style={style}>
		<SectionHeading>{heading}</SectionHeading>
		<div style={{ color: "#666", fontSize: 13, margin: "4px 0 10px" }}>
			{description}
		</div>
		<EditableInfoField {...fieldProps} />
	</div>
);
